import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useOrganisation } from '../contexts/OrganisationContext';
import { OrganisationMember, OrganisationMemberCreate, OrganisationInvitation, RemoveMemberPreviewResponse } from '../types/api';
import apiService from '../services/api';
import toast from 'react-hot-toast';
import { getApiErrorMessage } from '../constants/messages';
import OrganisationTabs from '../components/Organisation/OrganisationTabs';
import {
  UserGroupIcon,
  PlusIcon,
  XMarkIcon,
  TrashIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

const OrganisationMembersPage: React.FC = () => {
  const { t } = (useTranslation as any)('organisations');
  const navigate = useNavigate();
  const { orgId } = useParams<{ orgId: string }>();
  const {
    currentOrganisation,
    currentUserRole,
    selectOrganisation,
    canManageMembers,
  } = useOrganisation();

  const [members, setMembers] = useState<OrganisationMember[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<OrganisationInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [inviteForm, setInviteForm] = useState<OrganisationMemberCreate>({
    email_utilisateur_invite: '',
    role_organisation: 'member',
  });

  // Asset transfer modal state
  const [showAssetTransferModal, setShowAssetTransferModal] = useState(false);
  const [assetTransferPreview, setAssetTransferPreview] = useState<RemoveMemberPreviewResponse | null>(null);
  const [memberToRemove, setMemberToRemove] = useState<{ userId: string; memberName: string } | null>(null);
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    if (orgId && (!currentOrganisation || currentOrganisation.id !== orgId)) {
      selectOrganisation(orgId).catch(() => {
        toast.error(t('members.loadOrgError'));
        navigate('/organisations');
      });
    }
  }, [orgId, currentOrganisation, selectOrganisation, navigate]);

  useEffect(() => {
    const loadMembers = async () => {
      if (!currentOrganisation) return;

      try {
        setLoading(true);
        const [membersData, invitationsResponse] = await Promise.all([
          apiService.getOrganisationMembers(currentOrganisation.id),
          apiService.getPendingOrganisationInvitations().catch(() => []),
        ]);

        setMembers(membersData);
        // Filter invitations for current org
        const orgInvitations = invitationsResponse.filter(
          (inv: OrganisationInvitation) => inv.organisation_id === currentOrganisation.id && inv.statut === 'PENDING'
        );
        setPendingInvitations(orgInvitations);
      } catch (error: unknown) {
        toast.error(t('members.loadMembersError'));
      } finally {
        setLoading(false);
      }
    };

    if (currentOrganisation) {
      loadMembers();
    }
  }, [currentOrganisation]);

  const handleInviteMember = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!canManageMembers()) {
      toast.error(t('members.permissions.noInvite'));
      return;
    }

    if (!inviteForm.email_utilisateur_invite.trim()) {
      toast.error(t('members.validation.emailRequired'));
      return;
    }

    setInviting(true);
    try {
      await apiService.inviteOrganisationMember(currentOrganisation!.id, inviteForm);
      toast.success(t('members.inviteModal.invitationSent', { email: inviteForm.email_utilisateur_invite }));
      setShowInviteModal(false);
      setInviteForm({ email_utilisateur_invite: '', role_organisation: 'member' });

      // Reload data
      const [membersData, invitationsResponse] = await Promise.all([
        apiService.getOrganisationMembers(currentOrganisation!.id),
        apiService.getPendingOrganisationInvitations(),
      ]);
      setMembers(membersData);
      const orgInvitations = invitationsResponse.filter(
        (inv: OrganisationInvitation) => inv.organisation_id === currentOrganisation!.id && inv.statut === 'PENDING'
      );
      setPendingInvitations(orgInvitations);
    } catch (error: unknown) {
      const errorMessage = getApiErrorMessage(error) || t('members.inviteModal.inviteError');
      toast.error(errorMessage);
    } finally {
      setInviting(false);
    }
  };

  const handleChangeRole = async (memberId: string, currentRole: string) => {
    if (!canManageMembers()) {
      toast.error(t('members.permissions.noChangeRole'));
      return;
    }

    const newRole = currentRole === 'admin' ? 'member' : 'admin';

    try {
      await apiService.updateOrganisationMember(currentOrganisation!.id, memberId, {
        role_organisation: newRole,
      });
      toast.success(t('members.roleUpdate.success'));

      // Update local state
      setMembers(prev =>
        prev.map(m => (m.id === memberId ? { ...m, role_organisation: newRole } : m))
      );
    } catch (error: unknown) {
      const errorMessage = getApiErrorMessage(error) || t('members.roleUpdate.error');
      toast.error(errorMessage);
    }
  };

  const handleRemoveMember = async (userId: string, memberName: string) => {
    if (!canManageMembers()) {
      toast.error(t('members.permissions.noRemove'));
      return;
    }

    setRemoving(true);
    try {
      // First call: Preview (without confirm_transfer)
      const response = await apiService.removeOrganisationMember(currentOrganisation!.id, userId, false);

      if (apiService.isRemoveMemberPreview(response)) {
        // If member has assets, show the transfer modal
        if (response.warning && response.requires_confirmation) {
          setMemberToRemove({ userId, memberName });
          setAssetTransferPreview(response);
          setShowAssetTransferModal(true);
        }
      } else {
        // No assets - member removed directly
        toast.success(t('members.removeMember.success'));
        setMembers(prev => prev.filter(m => m.utilisateur_id !== userId));
      }
    } catch (error: unknown) {
      const errorMessage = getApiErrorMessage(error) || t('members.removeMember.error');
      toast.error(errorMessage);
    } finally {
      setRemoving(false);
    }
  };

  const handleConfirmRemoveMember = async () => {
    if (!memberToRemove) return;

    setRemoving(true);
    try {
      // Second call: Confirm transfer
      const response = await apiService.removeOrganisationMember(
        currentOrganisation!.id,
        memberToRemove.userId,
        true
      );

      if (!apiService.isRemoveMemberPreview(response)) {
        toast.success(response.message || t('members.assetTransfer.transferSuccess'));
        setMembers(prev => prev.filter(m => m.utilisateur_id !== memberToRemove.userId));
      }

      // Close modal and reset state
      setShowAssetTransferModal(false);
      setAssetTransferPreview(null);
      setMemberToRemove(null);
    } catch (error: unknown) {
      const errorMessage = getApiErrorMessage(error) || t('members.assetTransfer.transferError');
      toast.error(errorMessage);
    } finally {
      setRemoving(false);
    }
  };

  const handleCancelRemoveMember = () => {
    setShowAssetTransferModal(false);
    setAssetTransferPreview(null);
    setMemberToRemove(null);
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'owner':
        return 'bg-yellow-500/12 text-yellow-400';
      case 'admin':
        return 'bg-blue-500/12 text-blue-400';
      default:
        return 'bg-surface-3 text-text-secondary';
    }
  };

  if (loading || !currentOrganisation) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-500 mx-auto"></div>
          <p className="mt-4 text-text-secondary">{t('members.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="py-8">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate(`/organisations/${orgId}/dashboard`)}
            className="text-sm text-accent-400 hover:text-accent-300 mb-2"
          >
            ← {t('members.backToDashboard')}
          </button>
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-text-primary">
                {currentOrganisation.nom}
              </h1>
              <p className="mt-2 text-text-secondary">
                {t('members.title')}
              </p>
            </div>
            {canManageMembers() && (
              <button
                onClick={() => setShowInviteModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-accent-600 hover:bg-accent-700"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                {t('members.inviteButton')}
              </button>
            )}
          </div>
        </div>

        <OrganisationTabs orgId={currentOrganisation.id} />

        {/* Pending Invitations */}
        {pendingInvitations.length > 0 && canManageMembers() && (
          <div className="bg-surface-2 shadow-card rounded-card p-6 mb-6">
            <h2 className="text-lg font-medium text-text-primary mb-4 flex items-center">
              <EnvelopeIcon className="h-5 w-5 mr-2 text-text-quaternary" />
              {t('members.pendingInvitations.title', { count: pendingInvitations.length })}
            </h2>
            <div className="space-y-3">
              {pendingInvitations.map((invitation) => (
                <div
                  key={invitation.id}
                  className="flex items-center justify-between p-3 bg-yellow-500/12 border border-yellow-500/20 rounded-card"
                >
                  <div>
                    <p className="text-sm font-medium text-text-primary">
                      {invitation.email_invite}
                    </p>
                    <p className="text-xs text-text-tertiary">
                      {t('members.pendingInvitations.role')} {invitation.role_organisation.toUpperCase()} • {t('members.pendingInvitations.expiresOn')}{' '}
                      {new Date(invitation.date_expiration).toLocaleDateString()}
                    </p>
                  </div>
                  <span className="text-xs px-2 py-1 rounded-full bg-yellow-500/12 text-yellow-400">
                    {t('members.pendingInvitations.status')}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Members List */}
        <div className="bg-surface-2 shadow-card rounded-card overflow-hidden">
          <div className="px-6 py-4 border-b border-border-1">
            <h2 className="text-lg font-medium text-text-primary">
              {t('members.activeMembers.title', { count: members.length })}
            </h2>
          </div>

          <div className="divide-y divide-border-1">
            {members.map((member) => (
              <div key={member.id} className="px-6 py-4 flex items-center justify-between hover:bg-surface-3">
                <div className="flex items-center space-x-4 flex-1">
                  <div className="flex-shrink-0">
                    <div className="h-10 w-10 rounded-full bg-blue-500/12 flex items-center justify-center">
                      <span className="text-blue-400 font-medium">
                        {member.prenom.charAt(0)}{member.nom.charAt(0)}
                      </span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-text-primary">
                      {member.prenom} {member.nom}
                      {member.auto_added && (
                        <span className="ml-2 text-xs text-text-tertiary">{t('members.activeMembers.autoAdded')}</span>
                      )}
                    </p>
                    <p className="text-sm text-text-tertiary">{member.email}</p>
                    <p className="text-xs text-text-quaternary">
                      {t('members.activeMembers.memberSince', { date: new Date(member.date_ajout).toLocaleDateString() })}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <span className={`px-3 py-1 text-xs font-semibold rounded-full ${getRoleBadgeColor(member.role_organisation)}`}>
                    {member.role_organisation.toUpperCase()}
                  </span>

                  {canManageMembers() && member.role_organisation !== 'owner' && (
                    <div className="flex space-x-2">
                      {currentUserRole === 'owner' && (
                        <button
                          onClick={() => handleChangeRole(member.id, member.role_organisation)}
                          className="text-sm text-accent-400 hover:text-accent-300"
                        >
                          {member.role_organisation === 'admin' ? t('members.activeMembers.demote') : t('members.activeMembers.promote')}
                        </button>
                      )}
                      <button
                        onClick={() => handleRemoveMember(member.utilisateur_id, `${member.prenom} ${member.nom}`)}
                        className="text-sm text-red-400 hover:text-red-300 flex items-center"
                      >
                        <TrashIcon className="h-4 w-4 mr-1" />
                        {t('members.activeMembers.remove')}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {members.length === 0 && (
              <div className="px-6 py-12 text-center">
                <UserGroupIcon className="mx-auto h-12 w-12 text-text-quaternary" />
                <p className="mt-2 text-sm text-text-tertiary">
                  {t('members.activeMembers.empty')}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Invite Member Modal */}
      {showInviteModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity" onClick={() => setShowInviteModal(false)}></div>

            <div className="inline-block align-bottom bg-surface-2 rounded-card px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="absolute top-0 right-0 pt-4 pr-4">
                <button
                  onClick={() => setShowInviteModal(false)}
                  className="bg-surface-2 rounded-md text-text-quaternary hover:text-text-tertiary focus:outline-none"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                  <h3 className="text-lg leading-6 font-medium text-text-primary mb-4">
                    {t('members.inviteModal.title')}
                  </h3>

                  <form onSubmit={handleInviteMember} className="space-y-4">
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-text-secondary">
                        {t('members.inviteModal.emailLabel')}
                      </label>
                      <input
                        type="email"
                        id="email"
                        value={inviteForm.email_utilisateur_invite}
                        onChange={(e) => setInviteForm({ ...inviteForm, email_utilisateur_invite: e.target.value })}
                        className="mt-1 block w-full border border-border-1 rounded-md shadow-sm py-2 px-3 bg-surface-3 text-text-primary focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                        placeholder={t('members.inviteModal.emailPlaceholder') as string}
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="role" className="block text-sm font-medium text-text-secondary">
                        {t('members.inviteModal.roleLabel')}
                      </label>
                      <select
                        id="role"
                        value={inviteForm.role_organisation}
                        onChange={(e) => setInviteForm({ ...inviteForm, role_organisation: e.target.value as 'admin' | 'member' })}
                        className="mt-1 block w-full border border-border-1 rounded-md shadow-sm py-2 px-3 bg-surface-3 text-text-primary focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                      >
                        <option value="member">{t('members.inviteModal.roleMember')}</option>
                        <option value="admin">{t('members.inviteModal.roleAdmin')}</option>
                      </select>
                      <p className="mt-1 text-xs text-text-tertiary">
                        {t('members.inviteModal.roleHint')}
                      </p>
                    </div>

                    <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                      <button
                        type="submit"
                        disabled={inviting}
                        className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-accent-600 text-base font-medium text-white hover:bg-accent-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                      >
                        {inviting ? t('members.inviteModal.sending') : t('members.inviteModal.sendButton')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowInviteModal(false)}
                        className="mt-3 w-full inline-flex justify-center rounded-md border border-border-1 shadow-sm px-4 py-2 bg-surface-3 text-base font-medium text-text-secondary hover:bg-surface-3 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500 sm:mt-0 sm:w-auto sm:text-sm"
                      >
                        {t('cancel', { ns: 'common' })}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Asset Transfer Confirmation Modal */}
      {showAssetTransferModal && assetTransferPreview && memberToRemove && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity" onClick={handleCancelRemoveMember}></div>

            <div className="inline-block align-bottom bg-surface-2 rounded-card px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-yellow-500/12 sm:mx-0 sm:h-10 sm:w-10">
                  <ExclamationTriangleIcon className="h-6 w-6 text-yellow-400" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
                  <h3 className="text-lg leading-6 font-medium text-text-primary">
                    {t('members.assetTransfer.title')}
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-text-tertiary">
                      {assetTransferPreview.message}
                    </p>

                    {/* Assets Summary */}
                    <div className="mt-4 p-4 bg-surface-3 rounded-card">
                      <h4 className="text-sm font-medium text-text-primary mb-2">
                        {t('members.assetTransfer.assetsToTransfer')}
                      </h4>
                      <ul className="text-sm text-text-secondary space-y-1">
                        <li>• {t('members.assetTransfer.projects', { count: assetTransferPreview.assets_preview.project_count })}</li>
                        <li>• {t('members.assetTransfer.environments', { count: assetTransferPreview.assets_preview.environment_count })}</li>
                        <li>• {t('members.assetTransfer.applications', { count: assetTransferPreview.assets_preview.application_count })}</li>
                      </ul>

                      {assetTransferPreview.assets_preview.projects.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-border-1">
                          <p className="text-xs text-text-tertiary mb-1">{t('members.assetTransfer.affectedProjects')}</p>
                          <ul className="text-xs text-text-secondary">
                            {assetTransferPreview.assets_preview.projects.map((project) => (
                              <li key={project.id}>- {project.nom}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>

                    <p className="mt-4 text-sm text-yellow-400">
                      {t('members.assetTransfer.gitlabWarning')}
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleConfirmRemoveMember}
                  disabled={removing}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  {removing ? t('members.assetTransfer.transferring') : t('members.assetTransfer.confirmButton')}
                </button>
                <button
                  type="button"
                  onClick={handleCancelRemoveMember}
                  disabled={removing}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-border-1 shadow-sm px-4 py-2 bg-surface-3 text-base font-medium text-text-secondary hover:bg-surface-3 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500 sm:mt-0 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  {t('cancel', { ns: 'common' })}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OrganisationMembersPage;
